import React, { useEffect, useState } from "react";
import { PHASE2_LOADING, REPORT_LOADING } from "../../lib/content";
import { Cat } from "lucide-react";

/**
 * Branded full-height loading state. Rotates step copy to fill the wait.
 * `variant` picks between phase2 (~20s) and report (~30-45s) copy.
 * `error` shows a retry button if present.
 */
export function StepLoading({ variant = "phase2", error, onRetry, onBack }) {
    const copy = variant === "phase2" ? PHASE2_LOADING : REPORT_LOADING;
    const [i, setI] = useState(0);
    useEffect(() => {
        const t = setInterval(
            () => setI((v) => (v + 1) % copy.steps.length),
            variant === "phase2" ? 3200 : 4500,
        );
        return () => clearInterval(t);
    }, [copy.steps.length, variant]);

    return (
        <div
            className="flex min-h-[calc(100dvh-90px)] flex-col justify-between px-1 pb-8 pt-2"
            data-testid={`step-loading-${variant}`}
        >
            <div className="flex flex-1 flex-col items-center justify-center text-center">
                {variant === "report" && (
                    <span className="eyebrow mb-6 text-meow-teal">
                        {copy.badge}
                    </span>
                )}

                <PulseRing />

                <h2
                    className="mt-8 font-display text-[1.6rem] font-medium leading-tight text-meow-charcoal"
                    data-testid="loading-title"
                >
                    {copy.title}
                </h2>
                <p className="mt-3 max-w-[24rem] text-[14px] leading-relaxed text-meow-muted">
                    {copy.subtitle}
                </p>

                {!error && (
                    <div
                        className="mt-8 flex min-h-[26px] items-center gap-2 text-[13px] font-medium text-meow-teal"
                        data-testid="loading-step-text"
                    >
                        <span className="meow-dot" />
                        <span className="meow-dot" />
                        <span className="meow-dot" />
                        <span className="ml-1">{copy.steps[i]}</span>
                    </div>
                )}
            </div>

            {error && (
                <div
                    className="mx-1 rounded-2xl border border-meow-red/25 bg-meow-emergency p-4"
                    data-testid="loading-error"
                >
                    <div className="font-display text-sm font-medium text-meow-red">
                        We couldn&apos;t finish that step.
                    </div>
                    <p className="mt-1 text-[13px] leading-relaxed text-meow-charcoal/85">
                        {error}
                    </p>
                    <div className="mt-3 flex gap-2">
                        <button
                            type="button"
                            className="meow-btn-secondary flex-1"
                            onClick={onBack}
                            data-testid="loading-back-btn"
                        >
                            Go back
                        </button>
                        <button
                            type="button"
                            className="meow-btn-primary flex-1"
                            onClick={onRetry}
                            data-testid="loading-retry-btn"
                        >
                            Try again
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function PulseRing() {
    return (
        <div className="relative flex h-32 w-32 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-meow-teal/10 animate-ping" />
            <span className="absolute inset-3 rounded-full bg-meow-teal/15" />
            <span className="absolute inset-6 rounded-full bg-meow-teal/25" />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-meow-teal text-white shadow-[0_10px_30px_rgba(42,139,139,0.35)]">
                <Cat className="h-7 w-7" strokeWidth={1.75} aria-hidden />
            </div>
        </div>
    );
}
